/**
 * ABDM §3.2.6 Find bridge by service id, as the admin app shows it: are a
 * doctor's facility ids registered under DRCFO's Bridge ID (client id)?
 *
 * drcfo.backend files one status per id on the doctor
 * (services/abdmServiceCheck.ts): the HIP ID's as `abdmServiceStatus`, the HIU
 * ID's as `abdmHiuStatus` (the lookup in the HIU role, so `ours` needs the
 * gateway's `isHiu`) and the HFR ID's as `abdmHfrStatus` (the facility: linked
 * when a service of ours is `<id>` or `<id>_<n>`, listed in `linkedServiceIds`).
 * Each is `{ serviceId, verdict, checkedAt, lastError?, lastErrorAt? }` plus
 * the registration the gateway answered (`bridgeId`, `name`, `registrationId`,
 * `active`, `isHip`, `isHiu`, `isPhr`, `registerTime`, `dateCreated`,
 * `dateModified`). The HIP ID's is filed after a profile save too; all three
 * when an admin presses "Verify ABDM IDs" (POST /admin/doctor/:id/abdm-check).
 * A verdict is about its `serviceId` only: once the id changes it says nothing.
 * The wording follows the backend's `abdmServiceMessage` and the doctor app's
 * profile alert.
 */

/** The three ids, the status each is filed under, and the role it is judged in. */
export const ABDM_ID_FIELDS = {
  hipId: { key: "hip", label: "HIP ID", article: "a", status: "abdmServiceStatus", role: "HIP" },
  hiuId: { key: "hiu", label: "HIU ID", article: "a", status: "abdmHiuStatus", role: "HIU" },
  hfrId: { key: "hfr", label: "HFR ID", article: "an", status: "abdmHfrStatus", role: "HIP" },
};
export const ABDM_ID_FIELD_NAMES = Object.keys(ABDM_ID_FIELDS);

/** The id a manual lookup in `role` is worded as. */
export const abdmFieldForRole = (role) => (role === "HIU" ? "hiuId" : "hipId");

/** A 14-digit ABHA or HPR number, with or without its hyphens. */
const PERSON_NUMBER = /^\d{2}-?\d{4}-?\d{4}-?\d{4}$/;
/** What abdm.backend sends to the gateway at all (its SERVICE_ID). */
const SERVICE_ID = /^[A-Za-z0-9._-]{1,64}$/;

const looksLike = (id) => {
  if (PERSON_NUMBER.test(id)) return "It looks like an ABHA or HPR number";
  if (id.includes("@")) return "It looks like an ABHA or HPR address";
  return "A Service ID has only letters, digits and _";
};

const FAILURES = {
  not_a_service_id: {
    label: "Not a Service ID",
    severity: "error",
    title: ({ label, article }) => `This is not ${article} ${label}`,
    message: ({ label }, id) =>
      `The ${label} ${id} is not a facility Service ID. ${looksLike(id)}; the ${label} is the Service ID from the ABDM facility registration.`,
  },
  not_found: {
    label: "Not found",
    severity: "error",
    title: ({ label }) => `${label} not found in ABDM`,
    message: ({ label }, id, bridgeId, field) =>
      field === "hfrId"
        ? `No facility with the HFR ID ${id} is linked to DRCFO in ABDM, and ABDM has no service registered with it.`
        : `ABDM has no facility registered with the ${label} ${id}.`,
  },
  other_bridge: {
    label: "Other bridge",
    severity: "error",
    title: ({ label }) => `${label} is linked to another software`,
    message: (_, id, bridgeId, field) => {
      const bridge = bridgeId ? ` (${bridgeId})` : "";
      if (field === "hfrId")
        return `The facility with the HFR ID ${id} is linked to another software bridge${bridge}, not to DRCFO.`;
      if (field === "hiuId")
        return `The HIU ID ${id} is registered with another software bridge${bridge}, so the records DRCFO requests as a HIU will not reach DRCFO.`;
      return `The HIP ID ${id} is registered with another software bridge${bridge}, so ABHA record sharing will not reach DRCFO.`;
    },
  },
  inactive: {
    label: "Inactive",
    severity: "warning",
    title: ({ label }) => `${label} is inactive in ABDM`,
    message: ({ label }, id) => `The ${label} ${id} is linked to DRCFO but ABDM marks it inactive.`,
  },
  not_hip: {
    label: "Not a HIP",
    severity: "warning",
    title: ({ label }) => `${label} is not registered as a HIP`,
    message: ({ label }, id) => `The ${label} ${id} is linked to DRCFO but not registered as a HIP.`,
  },
  not_hiu: {
    label: "Not a HIU",
    severity: "warning",
    title: ({ label }) => `${label} is not registered as a HIU`,
    message: ({ label }, id) => `The ${label} ${id} is linked to DRCFO but not registered as a HIU.`,
  },
};

/** Why a check got no answer (abdm.backend and drcfo.backend reasons). */
const REASONS = {
  not_configured: "the ABDM service is not configured on this server",
  no_session: "the request carried no session",
  timeout: "ABDM did not answer in time",
  no_response: "the ABDM service could not be reached",
  unexpected_response: "the ABDM service sent an answer it could not read",
  session: "no ABDM gateway session could be opened",
  gateway_error: "the ABDM gateway answered with an error",
  bridge_error: "the bridge's services list could not be read",
};

const trimmed = (value) => String(value ?? "").trim();

const field = (name) => ABDM_ID_FIELDS[name] || ABDM_ID_FIELDS.hipId;

/**
 * A HIU ID with no verdict of its own, judged by the HIP ID's registration when
 * it is the same Service ID: the gateway's `isHiu` flag of a registration of
 * ours, or the HIP ID's own failure. drcfo.backend's doctors-list filter reads
 * the same thing, so a HIU ID checked before "Verify ABDM IDs" existed keeps its
 * badge until it is checked in its own right.
 */
const derivedHiu = (doctor, hiuId) => {
  const hip = doctor?.abdmServiceStatus;
  if (!hip || hip.serviceId !== hiuId) return null;
  if (["ours", "not_hip"].includes(hip.verdict)) {
    if (hip.isHiu === true) return { status: hip, verdict: "ours", derived: true };
    if (hip.isHiu === false) return { status: hip, verdict: "not_hiu", derived: true };
    return null;
  }
  if (FAILURES[hip.verdict]) return { status: hip, verdict: hip.verdict, derived: true };
  return null;
};

/**
 * What is known about the id now on the profile: `{ status, verdict, derived }`,
 * the status being the one filed for this very id (or the HIP ID's, for a HIU
 * ID judged through it, `derived`), `verdict` null when it holds none. Null
 * with no id.
 */
export function abdmCurrentStatus(doctor, name = "hipId") {
  const id = trimmed(doctor?.[name]);
  if (!id) return null;
  const own = doctor?.[field(name).status];
  const about = own && own.serviceId === id ? own : null;
  if (about?.verdict) return { status: about, verdict: about.verdict, derived: false };
  if (name === "hiuId") {
    const derived = derivedHiu(doctor, id);
    if (derived) return derived;
  }
  return { status: about, verdict: null, derived: false };
}

const yesNo = (value) => (typeof value === "boolean" ? (value ? "Yes" : "No") : "");

// The gateway writes `yyyy-MM-dd HH:mm:ss.SSSS` with no time zone, so the text
// is shown as it is, less the fraction of a second.
const gatewayTime = (value) => {
  const text = trimmed(value);
  const match = /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})\.\d+$/.exec(text);
  return match ? match[1] : text;
};

/**
 * A registration as the gateway answered it, as `[{ label, value }]` ("" for a
 * field it left out; the PHR flag only when it came). Empty when none came
 * (no `bridgeId`: not found, never checked).
 */
export function abdmRegistrationRowsOf(status) {
  if (!status || !trimmed(status.bridgeId)) return [];
  return [
    { label: "Facility Name", value: trimmed(status.name) },
    { label: "Registered Bridge ID", value: trimmed(status.bridgeId) },
    { label: "Registration ID", value: trimmed(status.registrationId) },
    { label: "Active in ABDM", value: yesNo(status.active) },
    { label: "Registered as HIP", value: yesNo(status.isHip) },
    { label: "Registered as HIU", value: yesNo(status.isHiu) },
    ...(typeof status.isPhr === "boolean"
      ? [{ label: "Registered as PHR", value: yesNo(status.isPhr) }]
      : []),
    { label: "Registered On", value: gatewayTime(status.registerTime) },
    { label: "Record Created", value: gatewayTime(status.dateCreated) },
    { label: "Last Modified", value: gatewayTime(status.dateModified) },
  ];
}

/**
 * The id's registration as the gateway answered the last check, for the
 * profile page. Empty when there is none to show: the status is about an
 * older id, or no registration came.
 */
export function abdmRegistrationRows(doctor, name = "hipId") {
  return abdmRegistrationRowsOf(abdmCurrentStatus(doctor, name)?.status);
}

/**
 * The doctors list's badge for one id: `{ label, tone }` with tone one of
 * "success" | "error" | "warning" | "default", or null with no id.
 */
export function abdmServiceBadge(doctor, name = "hipId") {
  const current = abdmCurrentStatus(doctor, name);
  if (!current) return null;
  if (current.verdict === "ours") return { label: "Verified", tone: "success" };
  const failure = FAILURES[current.verdict];
  if (failure) return { label: failure.label, tone: failure.severity };
  return { label: "Not checked", tone: "default" };
}

/**
 * `{ severity, title, message }` when the verdict for the id now on the
 * profile is a failure; null otherwise.
 */
export function abdmServiceAlert(doctor, name = "hipId") {
  const current = abdmCurrentStatus(doctor, name);
  const failure = FAILURES[current?.verdict];
  if (!failure) return null;
  const id = trimmed(doctor[name]);
  return {
    severity: failure.severity,
    title: failure.title(field(name)),
    message: failure.message(field(name), id, current.status?.bridgeId, name),
  };
}

/** Plain-words reason for an `unavailable` check. */
export function abdmUnavailableReason(reason) {
  const text = String(reason ?? "");
  if (REASONS[text]) return REASONS[text];
  const status = /^abdm_(\d{3})$/.exec(text);
  if (status) return `the ABDM service answered HTTP ${status[1]}`;
  const bridge = /^bridge_(\d{3})$/.exec(text);
  if (bridge) return `the bridge's services list answered HTTP ${bridge[1]}`;
  return "the check got no answer";
}

/**
 * What a check reports for the `result` the backend answered for one id:
 * `{ severity, message }`. "Verify ABDM IDs" files the result and notifies the
 * doctor (`filed`); the ABDM Bridge page's manual lookup files nothing.
 */
export function abdmCheckOutcome(result, name = "hipId", { filed = true } = {}) {
  const { label } = field(name);
  const id = trimmed(result?.serviceId);
  switch (result?.verdict) {
    case "ours": {
      const linked = Array.isArray(result.linkedServiceIds)
        ? result.linkedServiceIds.filter(Boolean)
        : [];
      const facility = result.name ? ` (${result.name})` : "";
      const services = linked.length
        ? `, as Service ID${linked.length > 1 ? "s" : ""} ${linked.join(", ")}`
        : "";
      return {
        severity: "success",
        message: `The ${label} ${id} is registered under DRCFO's Bridge ID${facility}${services}.`,
      };
    }
    case "not_set":
      return { severity: "info", message: `This doctor has no ${label} to check.` };
    case "unavailable":
      return {
        severity: "warning",
        message: `ABDM could not be asked: ${abdmUnavailableReason(result.reason)}. ${filed ? "Any earlier result is kept; try again later." : "Try again later."}`,
      };
    default: {
      const failure = FAILURES[result?.verdict];
      if (!failure) {
        return { severity: "warning", message: "The check returned no result." };
      }
      const notified = filed
        ? " The doctor gets an in-app notification when this result is new."
        : "";
      return {
        severity: failure.severity,
        message: `${failure.title(field(name))}. ${failure.message(field(name), id, result.bridgeId, name)}${notified}`,
      };
    }
  }
}

/**
 * What a value typed into the ABDM Bridge page's lookup box looks like, before
 * ABDM is asked: "" for a plausible Service ID, else why it is not one. It is
 * a hint, never a block: abdm.backend judges the value and answers
 * `not_a_service_id` for the same cases.
 */
export function abdmLookupHint(value) {
  const id = trimmed(value);
  if (!id) return "";
  if (PERSON_NUMBER.test(id))
    return "This looks like an ABHA or HPR number, not a facility Service ID.";
  if (id.includes("@")) return "This looks like an ABHA or HPR address, not a facility Service ID.";
  if (!SERVICE_ID.test(id)) return "A Service ID has only letters, digits and _.";
  return "";
}

/** The facility's HFR ID behind a numbered Service ID (`IN0110005723_39` → `IN0110005723`). */
export const baseServiceId = (id) => trimmed(id).replace(/_\d+$/, "");

/**
 * Which of a profile's ids a looked-up Service ID is: `["HIP ID", "HFR ID"]`
 * and the like (an HFR ID also when the lookup was for one of its numbered
 * services), as the lookup's holders table lists them.
 */
export function abdmIdsHolding(doctor, serviceId) {
  const id = trimmed(serviceId);
  return ABDM_ID_FIELD_NAMES.filter((name) => {
    const own = trimmed(doctor?.[name]);
    if (!own) return false;
    return name === "hfrId" ? own === id || own === baseServiceId(id) : own === id;
  }).map((name) => ABDM_ID_FIELDS[name].label);
}

/**
 * The profiles among `doctors` holding `serviceId` as one of their ids, each
 * with the ids it is held as (`abdmIdsHolding`), by name. The Bridge page's
 * services table names them beside each Service ID.
 */
export function abdmServiceHolders(doctors, serviceId) {
  return (Array.isArray(doctors) ? doctors : [])
    .map((doctor) => ({ doctor, heldAs: abdmIdsHolding(doctor, serviceId) }))
    .filter(({ heldAs }) => heldAs.length > 0)
    .sort((a, b) =>
      String(a.doctor?.name || "").localeCompare(String(b.doctor?.name || ""))
    );
}
